import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import MaskedInput from 'react-text-mask';
import { criarProcuracaoExterno } from '~/Services/Service';
import { inputStyles } from '~/Styles/inputStyles';
import { MASK_CPF, MASK_CNPJ } from '~/constant';
import { validarCPF, validarCNPJ } from '~/Utils/Helpers';

const limparMascara = (valor) => (valor ? valor.replace(/\D/g, '') : '');

const LABEL_ORIGEM = {
  GRID: 'já adicionado ao formulário',
  RAZAO: 'já existe como Razão Social',
  SOCIO: 'já existe como Sócio',
  EXTERNO: 'já existe como Externo cadastrado',
};

export default function ModalCadastroExterno({
  show,
  onHide,
  onSucesso,
  documentosConhecidos,
}) {
  const [tipoPessoa, setTipoPessoa] = useState('PF');
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [rg, setRg] = useState('');
  const [oab, setOab] = useState('');
  const [endereco, setEndereco] = useState('');
  const [representanteLegal, setRepresentanteLegal] = useState('');
  const [qualificacao, setQualificacao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState({});

  useEffect(() => {
    if (show) {
      setTipoPessoa('PF');
      setNome('');
      setCpf('');
      setCnpj('');
      setRg('');
      setOab('');
      setEndereco('');
      setRepresentanteLegal('');
      setQualificacao('');
      setErros({});
    }
  }, [show]);

  const validar = () => {
    const novosErros = {};
    if (!nome.trim()) novosErros.nome = 'Nome obrigatório';
    let docLimpo = '';
    if (tipoPessoa === 'PF') {
      docLimpo = limparMascara(cpf);
      if (!docLimpo) novosErros.cpf = 'CPF obrigatório';
      else if (!validarCPF(docLimpo)) novosErros.cpf = 'CPF inválido';
    }
    if (tipoPessoa === 'PJ') {
      docLimpo = limparMascara(cnpj);
      if (!docLimpo) novosErros.cnpj = 'CNPJ obrigatório';
      else if (!validarCNPJ(docLimpo)) novosErros.cnpj = 'CNPJ inválido';
    }
    const campoDoc = tipoPessoa === 'PF' ? 'cpf' : 'cnpj';
    if (docLimpo && !novosErros[campoDoc]) {
      const match = (documentosConhecidos || []).find(
        (d) => limparMascara(d.documento) === docLimpo
      );
      if (match) {
        const rotulo = LABEL_ORIGEM[match.origem] || 'já existe no sistema';
        const label = tipoPessoa === 'PF' ? 'CPF' : 'CNPJ';
        novosErros[campoDoc] = `${label} ${rotulo}: ${match.nome}. Selecione-o na lista.`;
      }
    }
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const handleSalvar = async () => {
    if (!validar()) return;
    setSalvando(true);
    try {
      const payload = {
        tipo_pessoa: tipoPessoa,
        nome: nome.trim(),
        cpf: tipoPessoa === 'PF' ? limparMascara(cpf) : null,
        cnpj: tipoPessoa === 'PJ' ? limparMascara(cnpj) : null,
        rg: tipoPessoa === 'PF' ? (rg.trim() || null) : null,
        oab: tipoPessoa === 'PF' ? (oab.trim() || null) : null,
        endereco: endereco.trim() || null,
        representante_legal: tipoPessoa === 'PJ' ? (representanteLegal.trim() || null) : null,
        qualificacao: qualificacao.trim() || null,
      };
      await criarProcuracaoExterno(payload);
      toast.success('Externo cadastrado com sucesso!');
      onSucesso();
      onHide();
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao cadastrar externo';
      toast.error(msg);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="h6">Cadastrar Externo</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col>
            <Form.Check
              inline
              type="radio"
              id="tipo-pf"
              label="Pessoa Física"
              checked={tipoPessoa === 'PF'}
              onChange={() => { setTipoPessoa('PF'); setErros({}); }}
            />
            <Form.Check
              inline
              type="radio"
              id="tipo-pj"
              label="Pessoa Jurídica"
              checked={tipoPessoa === 'PJ'}
              onChange={() => { setTipoPessoa('PJ'); setErros({}); }}
            />
          </Col>
        </Row>

        <Row>
          <Col md={12} className="mb-2">
            <Form.Label className="small mb-0">Nome <span className="text-danger">*</span></Form.Label>
            <Form.Control
              placeholder={tipoPessoa === 'PF' ? 'Nome completo' : 'Razão social'}
              value={nome}
              onChange={(e) => { setNome(e.target.value); }}
              style={inputStyles}
              isInvalid={!!erros.nome}
            />
            {erros.nome && <small className="text-danger">{erros.nome}</small>}
          </Col>
        </Row>

        {tipoPessoa === 'PF' && (
          <>
            <Row>
              <Col md={6} className="mb-2">
                <Form.Label className="small mb-0">CPF <span className="text-danger">*</span></Form.Label>
                <MaskedInput
                  className={`form-control${erros.cpf ? ' is-invalid' : ''}`}
                  placeholder="000.000.000-00"
                  value={cpf}
                  onChange={(e) => { setCpf(e.target.value); }}
                  style={inputStyles}
                  mask={MASK_CPF}
                />
                {erros.cpf && <small className="text-danger">{erros.cpf}</small>}
              </Col>
              <Col md={6} className="mb-2">
                <Form.Label className="small mb-0">RG</Form.Label>
                <Form.Control
                  value={rg}
                  onChange={(e) => { setRg(e.target.value); }}
                  style={inputStyles}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6} className="mb-2">
                <Form.Label className="small mb-0">OAB</Form.Label>
                <Form.Control
                  placeholder="Ex: SP123456"
                  value={oab}
                  onChange={(e) => { setOab(e.target.value); }}
                  style={inputStyles}
                />
              </Col>
              <Col md={6} className="mb-2">
                <Form.Label className="small mb-0">Endereço</Form.Label>
                <Form.Control
                  value={endereco}
                  onChange={(e) => { setEndereco(e.target.value); }}
                  style={inputStyles}
                />
              </Col>
            </Row>
          </>
        )}

        {tipoPessoa === 'PJ' && (
          <>
            <Row>
              <Col md={6} className="mb-2">
                <Form.Label className="small mb-0">CNPJ <span className="text-danger">*</span></Form.Label>
                <MaskedInput
                  className={`form-control${erros.cnpj ? ' is-invalid' : ''}`}
                  placeholder="00.000.000/0000-00"
                  value={cnpj}
                  onChange={(e) => { setCnpj(e.target.value); }}
                  style={inputStyles}
                  mask={MASK_CNPJ}
                />
                {erros.cnpj && <small className="text-danger">{erros.cnpj}</small>}
              </Col>
              <Col md={6} className="mb-2">
                <Form.Label className="small mb-0">Endereço</Form.Label>
                <Form.Control
                  value={endereco}
                  onChange={(e) => { setEndereco(e.target.value); }}
                  style={inputStyles}
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mb-2">
                <Form.Label className="small mb-0">Representante Legal</Form.Label>
                <Form.Control
                  value={representanteLegal}
                  onChange={(e) => { setRepresentanteLegal(e.target.value); }}
                  style={inputStyles}
                />
              </Col>
            </Row>
          </>
        )}

        <Row>
          <Col md={12} className="mb-2">
            <Form.Label className="small mb-0">Qualificação</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={qualificacao}
              onChange={(e) => { setQualificacao(e.target.value); }}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={salvando}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleSalvar} disabled={salvando}>
          {salvando ? 'Salvando...' : 'Cadastrar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
